import { useState, useEffect, useRef } from 'react';
import { userInfoSync } from 'hub-http/userInfo';
import { Scopes } from '../../constants/Permissions';
import { useFetchCampaignsAssociationSettings, hasCampaignsReadAccess } from './useFetchCampaignsAssociationSettings';
import { useFetchAssociatedCampaigns } from './useFetchAssociatedCampaign';
import { useFetchAssociatedAssetObjectId } from './useFetchAssociatedAssetObjectId';
const MAX_RETRIES = 5;
const RETRY_INTERVAL = 2000; // 2 seconds

/**
 * This hook orchestrates 3 separate API calls to fetch campaign label data.
 * This is excessive complexity for the frontend and should be replaced with a single backend endpoint.
 *
 * Proposed BE endpoint:
 * GET /campaigns/v1/associations/{objectType}/{assetId}/label
 *
 * Request params:
 * - objectType: string (e.g., "0-1", "0-2")
 * - assetId: string | number
 * - crmIdProperty: string (e.g., "hs_object_id")
 *
 * Response:
 * {
 *   hasAccess: boolean,          // Combines scope check, gate check, and association settings validation
 *   campaign: CrmObject | null,  // The associated campaign object or null if none exists
 * }
 *
 * This would eliminate:
 * - 3 separate network requests
 * - Complex client-side permission logic
 * - CRM ID resolution logic
 * - Association settings parsing
 * - Multiple loading states
 * - Client-side retry logic
 */

export function useFetchCampaignsPlatformLabelData({
  objectType,
  assetId,
  crmIdProperty
}) {
  const [objectIdRetryCount, setObjectIdRetryCount] = useState(0);
  const [campaignsRetryCount, setCampaignsRetryCount] = useState(0);
  const objectIdTimerRef = useRef();
  const campaignsTimerRef = useRef();
  let hasCampaignsToolAccess = true;
  try {
    hasCampaignsToolAccess = userInfoSync().user.scopes.includes(Scopes.CAMPAIGNS);
  } catch (error) {
    // Rely on BE to return the correct value
  }
  const {
    data: associationSettings,
    loading: associationSettingsLoading
  } = useFetchCampaignsAssociationSettings();
  const {
    data: objectId,
    loading: objectIdLoading,
    refetch: refetchObjectId
  } = useFetchAssociatedAssetObjectId({
    objectType,
    assetId,
    crmIdProperty,
    skip: !hasCampaignsToolAccess
  });
  const {
    data: associatedCampaigns,
    loading: associatedCampaignsLoading,
    refetch: refetchCampaigns
  } = useFetchAssociatedCampaigns({
    objectType,
    objectId,
    associationSettings,
    isOneToMany: false,
    isFetchingAllObjectProperties: true,
    skip: !hasCampaignsToolAccess || !objectId
  });

  // Retry logic for objectId when it fails or returns empty
  useEffect(() => {
    if (objectIdTimerRef.current) {
      clearTimeout(objectIdTimerRef.current);
    }
    const shouldRetry = hasCampaignsToolAccess && !objectIdLoading && !objectId && objectIdRetryCount < MAX_RETRIES && refetchObjectId;
    if (shouldRetry) {
      objectIdTimerRef.current = setTimeout(() => {
        void refetchObjectId();
        setObjectIdRetryCount(prev => prev + 1);
      }, RETRY_INTERVAL);
    }
    return () => {
      if (objectIdTimerRef.current) {
        clearTimeout(objectIdTimerRef.current);
      }
    };
  }, [objectId, objectIdLoading, objectIdRetryCount, hasCampaignsToolAccess, refetchObjectId]);

  // Retry logic for campaigns when it fails or returns empty
  useEffect(() => {
    if (campaignsTimerRef.current) {
      clearTimeout(campaignsTimerRef.current);
    }
    const shouldRetry = hasCampaignsToolAccess && objectId && !associatedCampaignsLoading && (!associatedCampaigns || associatedCampaigns.length === 0) && campaignsRetryCount < MAX_RETRIES && refetchCampaigns;
    if (shouldRetry) {
      campaignsTimerRef.current = setTimeout(() => {
        void refetchCampaigns();
        setCampaignsRetryCount(prev => prev + 1);
      }, RETRY_INTERVAL);
    }
    return () => {
      if (campaignsTimerRef.current) {
        clearTimeout(campaignsTimerRef.current);
      }
    };
  }, [associatedCampaigns, associatedCampaignsLoading, campaignsRetryCount, objectId, hasCampaignsToolAccess, refetchCampaigns]);
  const loading = associationSettingsLoading || objectIdLoading || associatedCampaignsLoading;
  const hasAssociationSettings = Boolean(associationSettings && associationSettings[objectType] && hasCampaignsReadAccess(associationSettings, objectType));
  const associatedCampaign = associatedCampaigns === null || associatedCampaigns === void 0 ? void 0 : associatedCampaigns[0];
  return {
    hasAssociationSettings,
    associatedCampaign,
    loading
  };
}